import { AddressEx, TokenInfo } from './common'

export type ParamValue = string | ParamValue[]

export enum Operation {
  CALL = 0,
  DELEGATE = 1,
}

export type InternalTransaction = {
  operation: Operation
  to: string
  value: string | null
  data: string | null
  dataDecoded: DataDecoded | null
}

export type ValueDecodedType = InternalTransaction[]

export type Parameter = {
  name: string
  type: string
  value: ParamValue
  valueDecoded: ValueDecodedType | null
}

export type DataDecoded = {
  method: string
  parameters: Parameter[] | null
}

export enum TransactionStatus {
  AWAITING_CONFIRMATIONS = 'AWAITING_CONFIRMATIONS',
  AWAITING_EXECUTION = 'AWAITING_EXECUTION',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  WILL_BE_REPLACED = 'WILL_BE_REPLACED',
}

export enum TransferDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  UNKNOWN = 'UNKNOWN',
}

export enum TransactionTokenType {
  ERC20 = 'ERC20',
  ERC721 = 'ERC721',
  NATIVE_COIN = 'NATIVE_COIN',
}

export type Erc20Transfer = {
  type: TransactionTokenType.ERC20
  tokenAddress: string
  tokenName: string | null
  tokenSymbol: string | null
  logoUri: string | null
  decimals: number | null
  value: string
}

export type Erc721Transfer = {
  type: TransactionTokenType.ERC721
  tokenAddress: string
  tokenId: string
  tokenName: string | null
  tokenSymbol: string | null
  logoUri: string | null
}

export type NativeCoinTransfer = {
  type: TransactionTokenType.NATIVE_COIN
  value: string
}

export type TransferInfo = Erc20Transfer | Erc721Transfer | NativeCoinTransfer

export interface Transfer {
  type: 'Transfer'
  sender: AddressEx
  recipient: AddressEx
  direction: TransferDirection
  transferInfo: TransferInfo
}

export type SetFallbackHandler = {
  type: 'SET_FALLBACK_HANDLER'
  handler: AddressEx
}

export type AddOwner = {
  type: 'ADD_OWNER'
  owner: AddressEx
  threshold: number
}

export type RemoveOwner = {
  type: 'REMOVE_OWNER'
  owner: AddressEx
  threshold: number
}

export type SwapOwner = {
  type: 'SWAP_OWNER'
  oldOwner: AddressEx
  newOwner: AddressEx
}

export type ChangeThreshold = {
  type: 'CHANGE_THRESHOLD'
  threshold: number
}

export type ChangeImplementation = {
  type: 'CHANGE_IMPLEMENTATION'
  implementation: AddressEx
}

export type EnableModule = {
  type: 'ENABLE_MODULE'
  module: AddressEx
}

export type DisableModule = {
  type: 'DISABLE_MODULE'
  module: AddressEx
}

export type SetGuard = {
  type: 'SET_GUARD'
  guard: AddressEx
}

export type DeleteGuard = {
  type: 'DELETE_GUARD'
}

export type SettingsInfo =
  | SetFallbackHandler
  | AddOwner
  | RemoveOwner
  | SwapOwner
  | ChangeThreshold
  | ChangeImplementation
  | EnableModule
  | DisableModule
  | SetGuard
  | DeleteGuard

export type SettingsChange = {
  type: 'SettingsChange'
  dataDecoded: DataDecoded
  settingsInfo: SettingsInfo | null
}

export interface Custom {
  type: 'Custom'
  to: AddressEx
  dataSize: string
  value: string
  methodName: string | null
  actionCount: number | null
  isCancellation: boolean
}

export type MultiSend = {
  type: 'Custom'
  to: AddressEx
  dataSize: string
  value: string
  methodName: 'multiSend'
  actionCount: number
  isCancellation: boolean
}

export type Creation = {
  type: 'Creation'
  creator: AddressEx
  transactionHash: string
  implementation: AddressEx | null
  factory: AddressEx | null
}

export type TransactionInfo = Transfer | SettingsChange | Custom | MultiSend | Creation

export type ModuleExecutionInfo = {
  type: 'MODULE'
  address: AddressEx
}

export type MultisigExecutionInfo = {
  type: 'MULTISIG'
  nonce: number
  confirmationsRequired: number
  confirmationsSubmitted: number
  missingSigners: AddressEx[] | null
}

export type ExecutionInfo = ModuleExecutionInfo | MultisigExecutionInfo

export type TransactionSummary = {
  id: string
  timestamp: number
  txStatus: TransactionStatus
  txInfo: TransactionInfo
  executionInfo?: ExecutionInfo
  safeAppInfo?: SafeAppInfo
}

export type Transaction = {
  transaction: TransactionSummary
  conflictType: 'None' | 'HasNext' | 'End'
  type: 'TRANSACTION'
}

export type DateLabel = {
  timestamp: number
  type: 'DATE_LABEL'
}

/**
 * @see https://gnosis.github.io/safe-client-gateway/docs/routes/transactions/models/summary/enum.Label.html
 */
export enum LabelValue {
  Queued = 'Queued',
  Next = 'Next',
}

export type Label = {
  label: LabelValue
  type: 'LABEL'
}

export type ConflictHeader = {
  nonce: number
  type: 'CONFLICT_HEADER'
}

export type TransactionListItem = Transaction | Label | ConflictHeader

type Page<T> = {
  next?: string
  previous?: string
  results: Array<T>
}

export type TransactionListPage = Page<TransactionListItem>

export type MultisigTransactionRequest = {
  to: string
  value: string
  data: string | null
  nonce: string
  operation: Operation
  safeTxGas: string
  baseGas: string
  gasPrice: string
  gasToken: string
  refundReceiver: string | null
  safeTxHash: string
  sender: string
  signature?: string | null
  origin: string | null
}

/* Transaction details types */
export type SafeAppInfo = {
  name: string
  url: string
  logoUri: string
}

export type TransactionData = {
  hexData: string | null
  dataDecoded: DataDecoded | null
  to: AddressEx
  value: string | null
  operation: Operation
  addressInfoIndex: { [key: string]: AddressEx } | null
  trustedDelegateCallTarget: boolean
}

export type ModuleExecutionDetails = {
  type: 'MODULE'
  address: AddressEx
}

export type MultisigConfirmation = {
  signer: AddressEx
  signature: string | null
  submittedAt: number
}

export type MultisigExecutionDetails = {
  type: 'MULTISIG'
  submittedAt: number
  nonce: number
  safeTxGas: string
  baseGas: string
  gasPrice: string
  gasToken: string
  refundReceiver: AddressEx
  safeTxHash: string
  executor: AddressEx | null
  signers: AddressEx[]
  confirmationsRequired: number
  confirmations: MultisigConfirmation[]
  rejectors: AddressEx[] | null
  gasTokenInfo: TokenInfo | null
}

export type DetailedExecutionInfo = ModuleExecutionDetails | MultisigExecutionDetails

export type TransactionDetails = {
  txId: string
  executedAt: number | null
  txStatus: TransactionStatus
  txInfo: TransactionInfo
  txData: TransactionData | null
  detailedExecutionInfo: DetailedExecutionInfo | null
  txHash: string | null
  safeAppInfo: SafeAppInfo | null
}

/* Transaction details types end */

/* Transaction estimation types */

export type SafeTransactionEstimationRequest = {
  to: string
  value: string
  data: string
  operation: Operation
}

// CGW v2 response
export type SafeTransactionEstimation = {
  currentNonce: number
  recommendedNonce: number
  safeTxGas: string
}

/* Transaction estimation types end */
